import React, { useState, useEffect, useMemo } from 'react'
import AppDropdown from '../../../../components/AppDropdown'

/**
 * ProvisionAgentModal
 * -------------------
 * Modal dialog for provisioning a Bridge employee into Exotel as a WebRTC/SIP agent.
 * Allows the admin to confirm employee details and pick the target App under which
 * the agent will be provisioned.
 */
export default function ProvisionAgentModal({
  open,
  onClose,
  employee,
  apps = [],
  defaultAppId,
  onConfirm,
  provisioning = false,
}) {
  const [selectedAppId, setSelectedAppId] = useState(defaultAppId || '')
  const [appUserId, setAppUserId] = useState('')

  useEffect(() => {
    if (defaultAppId) {
      setSelectedAppId(defaultAppId)
    } else if (apps.length > 0) {
      setSelectedAppId(apps[0].id)
    }
  }, [defaultAppId, apps, open])

  useEffect(() => {
    if (employee) {
      setAppUserId(employee.email || employee.app_user_id || '')
    }
  }, [employee, open])

  const appOptions = useMemo(() => {
    return apps.map((a) => ({
      value: a.id,
      label: `${a.app_name || 'Unnamed App'} (${a.app_id})`,
    }))
  }, [apps])

  if (!open || !employee) return null

  const empName = employee.employee_name || employee.name || 'Employee'
  const empPhone = employee.phone_number || employee.phone || '—'
  const empDept = employee.department || employee.role || 'Staff'

  const handleConfirm = () => {
    if (!selectedAppId) {
      appToast.warning('Please select an Exotel App.')
      return
    }
    const cleanEmail = (appUserId || '').trim()
    if (!cleanEmail) {
      appToast.warning('Please enter a valid App User ID (Email).')
      return
    }
    onConfirm(selectedAppId, employee, cleanEmail)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-[2px]">
      <div className="w-full max-w-lg rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-2xl space-y-5 animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-outline-variant/20 pb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <span className="material-symbols-outlined text-lg">person_add</span>
            </div>
            <div>
              <h3 className="text-base font-bold text-on-surface">Provision Telephony Agent</h3>
              <p className="text-xs text-on-surface-variant">
                Configure VoIP WebRTC calling and SIP credentials for this employee.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={provisioning}
            className="text-on-surface-variant hover:text-on-surface p-1 rounded-lg hover:bg-surface-container-low transition-colors"
          >
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        </div>

        {/* Employee details summary */}
        <div className="rounded-xl border border-outline-variant/30 bg-surface-container-low/50 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-on-surface-variant">Employee Name</span>
            <span className="text-xs font-bold text-on-surface">{empName}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-on-surface-variant">Department / Role</span>
            <span className="text-xs font-medium text-on-surface">{empDept}</span>
          </div>
          <div className="space-y-1 pt-1">
            <label className="text-xs font-semibold text-on-surface-variant flex items-center justify-between">
              <span>App User ID (Exotel Email) <span className="text-red-500">*</span></span>
              <span className="text-[10px] text-on-surface-variant/80">Must be unique in Exotel</span>
            </label>
            <input
              type="email"
              value={appUserId}
              onChange={(e) => setAppUserId(e.target.value)}
              placeholder="e.g. user@tezerp.com"
              disabled={provisioning}
              className="w-full px-3 py-2 text-xs font-mono rounded-xl border border-outline-variant/40 bg-surface-container-lowest text-on-surface focus:ring-2 focus:ring-primary focus:outline-none transition-all"
            />
          </div>
          <div className="flex items-center justify-between pt-1">
            <span className="text-xs font-semibold text-on-surface-variant">Agent Mobile (PSTN)</span>
            <span className="text-xs font-mono text-on-surface">{empPhone}</span>
          </div>
        </div>

        {/* Target App Selector */}
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-on-surface uppercase tracking-wider block">
            Target Exotel App <span className="text-red-500">*</span>
          </label>
          <AppDropdown
            value={selectedAppId}
            onChange={(val) => setSelectedAppId(val)}
            options={appOptions}
            disabled={provisioning || apps.length === 0}
            placeholder={apps.length === 0 ? 'No apps available' : 'Select an Exotel App'}
          />
          <p className="text-[11px] text-on-surface-variant">
            The employee will be mapped to this App. If they were already registered in Exotel, their SIP credentials will be synced automatically.
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2 border-t border-outline-variant/20">
          <button
            type="button"
            onClick={onClose}
            disabled={provisioning}
            className="px-4 py-2 rounded-xl border border-outline-variant/40 hover:bg-surface-container-low text-xs font-semibold text-on-surface transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={provisioning || !selectedAppId}
            className="px-5 py-2 rounded-xl bg-primary hover:bg-primary/90 text-on-primary text-xs font-bold shadow-sm transition-colors flex items-center gap-1.5 disabled:opacity-50"
          >
            {provisioning ? (
              <>
                <span className="material-symbols-outlined text-sm animate-spin">progress_activity</span>
                <span>Provisioning…</span>
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-sm">check</span>
                <span>Confirm &amp; Provision</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
